import React from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import * as f from "../../utils/formatter";
import IconAngry from "../icons/IconAngry";
import { getClientDoctors } from "../calendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserDoctor } from "@fortawesome/free-solid-svg-icons";
import { useClientProblem } from "../client/hooks/useClientProblem.jsx";
import { useRoles } from "../../context/AuthenticationTokenContext.jsx";

function StartAdornment({ client, roles }) {
  const problem = useClientProblem(client, roles);
  const clientDoctors = getClientDoctors(client);

  return (
    <>
      <InputAdornment position="start" sx={{ marginRight: 0 }}>
        {problem == null ? null : (
          <Tooltip title={problem}>
            <IconAngry />
          </Tooltip>
        )}
        {clientDoctors.length === 0 ? null : (
          <Tooltip title={clientDoctors.join(", ")}>
            <FontAwesomeIcon
              style={{ color: "var(--mui-palette-info-indicator)" }}
              icon={faUserDoctor}
            />
          </Tooltip>
        )}
      </InputAdornment>
    </>
  );
}

const ClientAutocomplete = ({
  search,
  options,
  option,
  onChange,
  loading,
  disabled,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = React.useState("");
  const [activeSearch, setActiveSearch] = React.useState(false);
  const roles = useRoles();

  return (
    <>
      <input type="hidden" name="client_id" value={option?.id ?? ""} />
      <Autocomplete
        filterOptions={(x) => x}
        autoComplete
        disabled={disabled}
        loading={loading}
        options={activeSearch ? options : option ? [option] : []}
        value={option}
        inputValue={inputValue}
        getOptionLabel={(opt) => f.fullName(opt)}
        getOptionKey={(opt) => opt.id}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderInput={(params) => {
          const { InputProps, ...rest } = params;

          return (
            <TextField
              label={t("appointment.client")}
              required
              slotProps={{
                input: {
                  ...InputProps,
                  startAdornment: (
                    <StartAdornment client={option} roles={roles} />
                  ),
                },
              }}
              {...rest}
            />
          );
        }}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          const problem = useClientProblem(option, roles);
          const clientDoctors = getClientDoctors(option);

          return (
            <li key={key} {...otherProps}>
              <Box>
                <strong>{f.fullName(option)}</strong>{" "}
                {problem != null && (
                  <Tooltip title={problem}>
                    <IconAngry />
                  </Tooltip>
                )}
                {clientDoctors.length > 0 && (
                  <Tooltip title={clientDoctors.join(", ")}>
                    <FontAwesomeIcon
                      style={{ color: "var(--mui-palette-info-indicator)" }}
                      icon={faUserDoctor}
                    />
                  </Tooltip>
                )}
                <br />
                {t("client.birthNumber")}: {option.birth_number}{" "}
                {t("client.phone")}: {option.phone}
                <br />
                {option.street} {option.city}
                <br />
                {option.post_code}
              </Box>
            </li>
          );
        }}
        onInputChange={(_, term, reason) => {
          setInputValue(term);
          if (reason === "input") {
            const trimmed = term.trim();
            if (trimmed.length > 0) {
              setActiveSearch(true);
              search(trimmed);
            } else {
              // cleared
              setActiveSearch(false);
            }
          }
        }}
        onChange={(_, value) => {
          // Clear input after selection so focusing another field does not reuse the term
          if (value) {
            setInputValue(f.fullName(value));
            setActiveSearch(false); // lock list until user types again
          } else {
            setInputValue("");
            setActiveSearch(false);
          }
          onChange(value == null ? null : value);
        }}
        onBlur={() => {
          // If user leaves the field without a client chosen, clear any stray text
          if (!option) setInputValue("");
          setActiveSearch(false);
        }}
        onFocus={() => {
          // Do not auto-show previous global results
          if (inputValue.trim() === "") setActiveSearch(false);
        }}
      />
    </>
  );
};

export { ClientAutocomplete };
